import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Client, ClientsState, IndividualClient } from '../../types/client';
import { clientApiService } from '../../services/clientApiService';
import type { RootState } from '../store';

// Initial state
const initialState: ClientsState = {
  clients: [],
  loading: false,
  error: null
};

// Helper function to create a blank individual client
export function createBlankClient(): IndividualClient {
  return {
    id: uuidv4(),
    type: 'individual',
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    preferences: '',
    createdAt: new Date().toISOString()
  };
}

// Async thunks

/**
 * Fetch all clients from the API
 */
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async () => {
    return await clientApiService.fetchClients();
  }
);

/**
 * Add a new client
 */
export const addClient = createAsyncThunk(
  'clients/addClient',
  async (client: Client) => {
    return await clientApiService.createClient(client);
  }
);

/**
 * Update an existing client
 */
export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async (client: Client) => {
    return await clientApiService.updateClient(client);
  }
);

/**
 * Delete a client by ID
 */
export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id: string) => {
    await clientApiService.deleteClient(id);
    return id; // Return id for reducer to remove from state
  }
);

/**
 * Update a single field of a client
 */
export const updateClientField = createAsyncThunk(
  'clients/updateClientField',
  async ({ id, field, value }: { id: string; field: string; value: string }, { getState }) => {
    const state = getState() as RootState;
    const client = state.clients.clients.find(c => c.id === id);

    if (!client) {
      throw new Error(`Client ${id} not found`);
    }

    const updatedClient = { ...client, [field]: value };
    return await clientApiService.updateClient(updatedClient);
  }
);

/**
 * Delete all clients
 */
export const deleteAllClients = createAsyncThunk(
  'clients/deleteAllClients',
  async () => {
    await clientApiService.deleteAllClients();
  }
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch clients
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch clients';
      });

    // Add client
    builder
      .addCase(addClient.fulfilled, (state, action) => {
        state.clients.push(action.payload);
      })
      .addCase(addClient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add client';
      });

    // Update client
    builder
      .addCase(updateClient.fulfilled, (state, action) => {
        const index = state.clients.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update client';
      });

    // Update client field
    builder
      .addCase(updateClientField.fulfilled, (state, action) => {
        const index = state.clients.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClientField.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update client field';
      });

    // Delete client
    builder
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter(c => c.id !== action.payload);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete client';
      });

    // Delete all clients
    builder
      .addCase(deleteAllClients.fulfilled, (state) => {
        state.clients = [];
      })
      .addCase(deleteAllClients.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete all clients';
      });
  }
});

export default clientsSlice.reducer;
